import {
  Button,
  Table,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import PageFade from "../../components/animation/PageFade";
import {
  useGetProductsQuery,
  useDeleteProductMutation,
} from "../../services/api";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

// Optimized Product Image Component with lazy loading and placeholder
const ProductImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Check if image is already loaded (cached images)
    if (imgRef.current?.complete) {
      setIsLoading(false);
    }
  }, []);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div
        style={{
          width: "50px",
          height: "50px",
          backgroundColor: "#f0f0f0",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          color: "#999",
        }}
      >
        No Image
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "50px", height: "50px" }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "50px",
            height: "50px",
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <Spinner animation="border" size="sm" />
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        width={50}
        height={50}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          width: "50px",
          height: "50px",
          objectFit: "cover",
          borderRadius: "4px",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      />
    </div>
  );
};

export default function Products() {
  const [searchTerm, setSearchTerm] = useState("");
  const { data, error, isLoading, refetch } = useGetProductsQuery();
  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteProduct(id).unwrap();
        toast.success("Product deleted successfully");
      } catch (err: any) {
        toast.error(err?.data?.message || "Failed to delete product");
      }
    }
  };

  const products = data?.data || [];
  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTotalStock = (variants: any) => {
    let parsedVariants = variants || [];
    if (typeof parsedVariants === 'string') {
      try {
        parsedVariants = JSON.parse(parsedVariants);
      } catch (error) {
        console.error('Failed to parse variants string in Products list:', error);
        parsedVariants = [];
      }
    }
    return (
      (Array.isArray(parsedVariants) ? parsedVariants : []).reduce((sum, variant) => sum + (variant.stock || 0), 0) || 0
    );
  };

  if (isLoading) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageFade>
    );
  }

  if (error) {
    const errorMessage =
      (error as any)?.data?.message ||
      (error as any)?.error ||
      "Failed to load products. Please check your connection and try again.";
    const status = (error as any)?.status;

    return (
      <PageFade>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Products</Alert.Heading>
          <p>{errorMessage}</p>
          {status === 401 && (
            <p className="mb-0">
              <small>Your session may have expired. Please log in again.</small>
            </p>
          )}
          {status === 403 && (
            <p className="mb-0">
              <small>You don't have permission to access this resource.</small>
            </p>
          )}
          <hr />
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => refetch()}
              variant="outline-danger"
              size="sm"
            >
              Retry
            </Button>
          </div>
        </Alert>
      </PageFade>
    );
  }

  return (
    <PageFade>
      <Row className="mb-3">
        <Col>
          <h3>Products</h3>
        </Col>
        <Col className="text-end">
          <Button as={Link as any} to="/products/new">
            Add Product
          </Button>
        </Col> 
      </Row>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            placeholder="Search by name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>
      {filteredProducts.length === 0 ? (
        <Alert variant="info">
          No products found. Click "Add Product" to create your first product.
        </Alert>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th>Visibility</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => {
              const totalStock = getTotalStock(product.variants);
              return (
                <tr key={product._id}>
                    <td>
                      {product.images && product.images.length > 0 ? (
                        <ProductImage src={product.images[0]} alt={product.name} />
                      ) : (
                        <div
                          style={{
                            width: "50px",
                            height: "50px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            color: "#999",
                          }}
                        >
                          No Image
                        </div>
                      )}
                    </td>
                    <td>
                      <Link to={`/products/${product._id}`}>
                        {product.name}
                      </Link>
                    </td>
                    <td>{product.sku}</td>
                    <td>
                      {product.priceBeforeDiscount ? (
                        <>
                          <span className="text-decoration-line-through text-muted me-2">
                            EGP {product.priceBeforeDiscount}
                          </span>
                          <strong>EGP {product.price}</strong>
                        </>
                      ) : (
                        <strong>EGP {product.price}</strong>
                      )}
                    </td>
                    <td>{totalStock}</td>
                    <td>
                      <Badge bg={product.inStock ? "success" : "danger"}>
                        {product.inStock ? "In Stock" : "Out of Stock"}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={product.isVisible ? "primary" : "secondary"}>
                        {product.isVisible ? "Visible" : "Hidden"}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button
                        as={Link as any}
                        to={`/products/${product._id}`}
                        size="sm"
                        variant="outline-info"
                        className="me-2"
                      >
                        View
                      </Button>
                      <Button
                        as={Link as any}
                        to={`/products/${product._id}/edit`}
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(product._id, product.name)}
                        disabled={isDeleting}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      )}
    </PageFade>
  );
}
